import { Empty } from 'assets/Illustrations'
import Spinner from 'components/Spinner'
import { prettyPrice } from 'helpers/prettyData'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, Sector, Tooltip } from 'recharts'
const colors = [
	'#28a745',
	'#ED4C5C',
	'#007bff',
	'#FDB914',
	'#d9d9d9',
	'#B53471',
]

export default function Chart({ data = [], isLoading = false }) {
	const { i18n } = useTranslation('dashboard')
	const { language } = i18n
	const [activeIndex, setActiveIndex] = useState(null)

	const paddingAngle = useMemo(() => {
		const paddingAngle = data?.length > 1 ? 4 : 0
		return paddingAngle
	}, [data])
	const cornerRadius = useMemo(() => {
		const cornerRadius = data?.length > 1 ? 5 : 0
		return cornerRadius
	}, [data])
	const total = useMemo(() => {
		const total = data.reduce((total, item) => {
			total += item?.value || 0
			return total
		}, 0)
		return prettyPrice(total, language)
	}, [data])
	return isLoading ? (
		<div className='flex items-center justify-center w-full h-full min-h-[211px]'>
			<Spinner />
		</div>
	) : data?.length ? (
		<div className='flex flex-col items-center justify-evenly'>
			<div className='h-[280px] w-[280px] relative'>
				<div className='absolute top-0, left-0 flex items-center justify-center w-[280px] h-[280px]'>
					<div className='h-[110px] w-[110px] rounded-[55px] flex flex-col items-center justify-center gap-1 bg-[#F4F4F4] dark:bg-[#222222]'>
						<p className='text-[#28a745] text-[15px] font-bold'>
							{prettyPrice(data[0].value)}
						</p>
						<p className='text-[#292B32] dark:text-[#e1e1e1] text-[13px] font-bold'>
							{total}
						</p>
					</div>
				</div>
				<PieChart width={280} height={280}>
					<Pie
						activeIndex={activeIndex}
						animationBegin={0}
						cornerRadius={cornerRadius}
						data={data}
						dataKey='value'
						innerRadius={70}
						onMouseEnter={(_, index) => setActiveIndex(index)}
						onMouseLeave={() => setActiveIndex(-1)}
						outerRadius={100}
						paddingAngle={paddingAngle}
						stroke='#ffffff0'
						activeShape={props => (
							<Sector
								{...props}
								innerRadius={props?.innerRadius + 5}
								outerRadius={props.outerRadius + 10}
							/>
						)}
					>
						{data.map((_, i) => (
							<Cell key={i} fill={colors[i % colors?.length]} stroke='none' />
						))}
					</Pie>
					<Tooltip
						offset={20}
						cursor={{ fill: 'none' }}
						content={({ active, payload }) => {
							if (active && payload && payload?.length) {
								return (
									<div
										style={{
											backgroundColor: 'rgba(0, 0, 0, 0.8)',
											borderRadius: 12,
											color: '#ffffff',
											fontSize: 15,
											fontWeight: 500,
											padding: '10px 16px',
											userSelect: 'none',
											maxWidth: '300px',
										}}
									>
										<p>{payload?.[0]?.payload?.name}:</p>
										<p
											className={`ms-2`}
											style={{ color: payload[0]?.payload?.fill }}
										>
											{prettyPrice(payload[0].value)} so'm
										</p>
									</div>
								)
							}
							return null
						}}
					/>
				</PieChart>
			</div>
			<ul className='sm:flex items-center justify-center gap-6'>
				{data?.map((item, i) => (
					<li
						key={i}
						className='flex items-center gap-2 text-gray-900 dark:text-gray-200 m-0'
					>
						<span
							className='inline-block h-3.5 w-3.5 rounded-full border-[3px]'
							style={{
								borderColor: colors[i],
								background: 'transparent',
							}}
						/>
						<span className={`line-clamp-1`}>{item.name}</span>
					</li>
				))}
			</ul>
		</div>
	) : (
		<div className='flex flex-col items-center justify-center gap-4 w-full h-full'>
			<Empty />
			<span className='text-[#000000] dark:text-[#ffffff]'>Нет данных</span>
		</div>
	)
}
